import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { VehiclePerformanceData, FleetUtilization, VehicleTypeMetric } from '../../types/analytics';
import BarChart from '../Charts/BarChart';
import PieChart from '../Charts/PieChart';
import DataTable from '../Tables/DataTable';

interface VehiclePerformanceAnalyticsProps {
  startDate: Date;
  endDate: Date;
  topVehicleTypes: VehicleTypeMetric[];
}

const VehiclePerformanceAnalytics: React.FC<VehiclePerformanceAnalyticsProps> = ({
  startDate,
  endDate,
  topVehicleTypes
}) => {
  const [vehiclePerformance, setVehiclePerformance] = useState<VehiclePerformanceData[]>([]);
  const [fleetUtilization, setFleetUtilization] = useState<FleetUtilization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVehiclePerformanceData();
  }, [startDate, endDate]);

  const loadVehiclePerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [performanceResponse, utilizationResponse] = await Promise.all([
        analyticsService.getVehiclePerformance(),
        analyticsService.getFleetUtilization(startDate, endDate)
      ]);

      if (performanceResponse.success && performanceResponse.data) {
        setVehiclePerformance(performanceResponse.data);
      }

      if (utilizationResponse.success && utilizationResponse.data) {
        setFleetUtilization(utilizationResponse.data);
      }

      if (!performanceResponse.success) {
        setError(performanceResponse.error?.message || 'Failed to load vehicle performance data');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Error loading vehicle performance data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts
  const vehicleRevenueData = vehiclePerformance.slice(0, 10).map(vehicle => ({
    name: `${vehicle.vehicleBrand} ${vehicle.vehicleModel}`,
    revenue: vehicle.totalRevenue,
    bookings: vehicle.totalBookings
  }));

  const vehicleUtilizationData = fleetUtilization.slice(0, 10).map(vehicle => ({
    name: vehicle.vehicleName,
    utilization: vehicle.utilizationRate,
    hours: vehicle.hoursRented
  }));

  const vehicleTypeDistribution = topVehicleTypes.map((type, index) => ({
    name: type.vehicleType,
    value: type.count,
    color: [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
      '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
    ][index % 10]
  }));

  const rentalFrequencyData = topVehicleTypes.map(type => ({
    vehicleType: type.vehicleType,
    count: type.count,
    avgDuration: type.averageDuration,
    revenue: type.revenue
  }));

  // Table columns for vehicle performance
  const performanceColumns = [
    { 
      key: 'vehicleName', 
      title: 'Vehicle', 
      sortable: true,
      render: (value: string, row: VehiclePerformanceData) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.vehicleBrand} {row.vehicleModel}</div>
        </div>
      )
    },
    { 
      key: 'totalRevenue', 
      title: 'Revenue', 
      sortable: true,
      render: (value: number) => analyticsService.formatCurrency(value)
    },
    { 
      key: 'totalBookings', 
      title: 'Bookings', 
      sortable: true 
    },
    { 
      key: 'averageUtilization', 
      title: 'Utilization', 
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center">
          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${Math.min(value, 100)}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value.toFixed(1)}%</span>
        </div>
      )
    },
    { 
      key: 'totalHoursRented', 
      title: 'Hours Rented', 
      sortable: true,
      render: (value: number) => `${value.toFixed(1)}h`
    }
  ];

  // Table columns for rental frequency
  const frequencyColumns = [
    { key: 'vehicleType', title: 'Vehicle Type', sortable: true },
    { key: 'count', title: 'Rental Count', sortable: true },
    { 
      key: 'avgDuration', 
      title: 'Avg Duration', 
      sortable: true,
      render: (value: number) => `${value.toFixed(1)} days`
    },
    { 
      key: 'revenue', 
      title: 'Total Revenue', 
      sortable: true,
      render: (value: number) => analyticsService.formatCurrency(value)
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadVehiclePerformanceData}
              className="mt-2 text-sm text-red-800 underline hover:text-red-900"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Vehicle Revenue and Utilization Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarChart
          title="Top Vehicles by Revenue"
          data={vehicleRevenueData}
          xAxisKey="name"
          bars={[
            { key: 'revenue', color: '#10B981', name: 'Revenue ($)' }
          ]}
          height={350}
        />

        <BarChart
          title="Fleet Utilization Rates"
          data={vehicleUtilizationData}
          xAxisKey="name"
          bars={[
            { key: 'utilization', color: '#3B82F6', name: 'Utilization (%)' }
          ]}
          height={350}
        />
      </div>

      {/* Vehicle Type Distribution and Rental Frequency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieChart
          title="Most Rented Vehicle Types"
          data={vehicleTypeDistribution}
        />

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Rental Frequency Analytics</h3>
          <div className="space-y-4">
            {topVehicleTypes.slice(0, 5).map((type, index) => (
              <div key={type.vehicleType} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: vehicleTypeDistribution[index]?.color || '#6B7280' }}
                  ></div>
                  <div>
                    <div className="font-medium text-gray-900">{type.vehicleType}</div>
                    <div className="text-sm text-gray-500">
                      {type.count} rentals • {type.averageDuration.toFixed(1)} days avg
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-gray-900">
                    {analyticsService.formatCurrency(type.revenue)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {analyticsService.formatCurrency(type.revenue / type.count)} avg
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vehicle Performance Table */}
      <DataTable
        title="Vehicle Performance Details"
        data={vehiclePerformance}
        columns={performanceColumns}
        pageSize={10}
      />

      {/* Rental Duration Analytics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Rental Duration Analytics</h3>
        <BarChart
          data={rentalFrequencyData}
          xAxisKey="vehicleType"
          bars={[
            { key: 'avgDuration', color: '#8B5CF6', name: 'Avg Duration (days)' }
          ]}
          height={300}
        />
      </div>

      {/* Fleet Utilization Metrics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Fleet Utilization Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {fleetUtilization.length > 0 
                ? (fleetUtilization.reduce((sum, v) => sum + v.utilizationRate, 0) / fleetUtilization.length).toFixed(1)
                : '0'
              }%
            </div>
            <div className="text-sm text-gray-500">Average Fleet Utilization</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {fleetUtilization.reduce((sum, v) => sum + v.hoursRented, 0).toFixed(0)}
            </div>
            <div className="text-sm text-gray-500">Total Hours Rented</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">
              {analyticsService.formatCurrency(
                fleetUtilization.reduce((sum, v) => sum + v.revenue, 0)
              )}
            </div>
            <div className="text-sm text-gray-500">Total Fleet Revenue</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehiclePerformanceAnalytics;